import { MetronomeButton } from '@tunasong/audio-ui'
import { Pause, Play } from '@tunasong/icons'
import { rhythmToString } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isSong } from '@tunasong/schemas'
import { Button, HBox, IconButton, Tooltip, Typography } from '@tunasong/ui-lib'
import { type FC, type ReactNode } from 'react'
import { PartLineText } from './part/part-line-text.js'
import styles from './performance-header.module.css'

import type { HTMLAttributes } from 'react'

export interface PerformanceHeaderProps extends HTMLAttributes<HTMLDivElement> {
  currentEntity?: Persisted<Entity>
  currentLayoutName: string
  flashMetronomeBackground: boolean
  /** Leave performance mode */
  onExit: () => void
  /** Autoscroll state */
  isAutoscrollPlaying?: boolean
  onAutoscrollToggle?: () => void
  /** Estimated or inferred duration in seconds */
  autoscrollDuration?: number | null
  /** "Playing Vocals + Guitar ▾" — the performer's part menu. */
  partMenu?: ReactNode
  /** "Shapes | Concert": present only with a capo and Guitar among the parts. */
  pitchFrame?: ReactNode
  /** "My notes (n)" toggle. */
  notesToggle?: ReactNode
  /** The Display popover (text size, notes placement, columns, flash). */
  display?: ReactNode
  /** Phones move the metronome to the bottom bar. */
  showMetronome?: boolean
  /** What the performer's parts need in the header ("♩=92 · 4/4 · straight · count-in 1 bar · 4"); replaces the plain rhythm text. */
  partLine?: string
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) {
    return '--:--'
  }
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export const PerformanceHeader: FC<PerformanceHeaderProps> = props => {
  const {
    currentEntity,
    flashMetronomeBackground = false,
    currentLayoutName: _currentLayoutName,
    isAutoscrollPlaying = false,
    onAutoscrollToggle,
    autoscrollDuration = 180,
    partMenu,
    pitchFrame,
    notesToggle,
    display,
    partLine,
    showMetronome = true,
    onExit,
    ...divProps
  } = props

  return (
    <>
      <div
        {...divProps}
        style={divProps.style}
        className={[styles.headerWrapper, divProps.className].filter(Boolean).join(' ')}
      >
      <div className={styles.header}>
        <HBox className={styles.leftSection}>
          <Button variant="outlined" onClick={onExit}>
            Exit
          </Button>
          {onAutoscrollToggle && (
            <HBox className={styles.autoscrollBox}>
              <Tooltip title={isAutoscrollPlaying ? 'Pause autoscroll' : 'Start autoscroll'}>
                <IconButton
                  onClick={onAutoscrollToggle}
                  color={isAutoscrollPlaying ? 'secondary' : 'primary'}
                  aria-label={isAutoscrollPlaying ? 'Pause autoscroll' : 'Start autoscroll'}
                >
                  {isAutoscrollPlaying ? <Pause /> : <Play />}
                </IconButton>
              </Tooltip>
              <Typography variant="caption" color="primary" className={styles.durationText}>
                {formatDuration(autoscrollDuration)}
              </Typography>
            </HBox>
          )}
        </HBox>
        {isSong(currentEntity) ? (
          <>
            <HBox className={styles.rhythmSection}>
              {currentEntity.rhythm || partLine ? (
                <div className={styles.rhythmContainer}>
                  {currentEntity.rhythm && showMetronome ? (
                    <MetronomeButton
                      size="medium"
                      rhythm={currentEntity.rhythm}
                      flashBackground={flashMetronomeBackground}
                    />
                  ) : null}
                  <Typography component="span" className={styles.rhythmText}>
                    {partLine ? <PartLineText line={partLine} /> : rhythmToString(currentEntity.rhythm)}
                  </Typography>
                </div>
              ) : null}
            </HBox>
          </>
        ) : (
          <div />
        )}

        <HBox className={styles.rightSection}>
          {partMenu}
          {pitchFrame}
          {notesToggle}
          {display}
        </HBox>
      </div>
      <div id="performance-structure-map" className={styles.structureMapSlot} />
      </div>
    </>
  )
}

export default PerformanceHeader
